import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Star, Phone, Globe, Clock, Trash2, Search, Sparkles, Link2 } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { celebrationService } from '@/lib/api/celebration.service';
import { venueService } from '@/lib/api/venue.service';
import { venueRoomService } from '@/lib/api/venue-room.service';
import type { Celebration } from '@/types/celebration.types';
import type {
  CelebrationVenueSearch,
  Venue,
  VenueIntentFilter,
  VenueSearchRequest,
  VenueWithGoogleDetails,
  GooglePlaceSearchResult,
} from '@/types/venue.types';
import type { VenueRoomWithConfigs } from '@/types/venue-room.types';

type SearchSuggestion = GooglePlaceSearchResult & {
  _db_venue_id?: number;
  is_saved?: boolean;
};

const venueIntentOptions: VenueIntentFilter[] = [
  'Rooftop',
  'Wedding Venue',
  'Guest Accommodation',
  'Outdoor',
  'Garden',
  'Banquet Hall',
  'Waterfront',
  'Luxury',
];

export const VenueList: React.FC = () => {
  const { t } = useTranslation(['venues', 'common']);
  const navigate = useNavigate();
  const { celebrationId } = useParams<{ celebrationId?: string }>();
  const celebrationIdNum = celebrationId ? Number(celebrationId) : undefined;
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchSuggestion[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [savingPlaceId, setSavingPlaceId] = useState<string | null>(null);
  const [savingVenueId, setSavingVenueId] = useState<number | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<Record<number, boolean>>({});
  const [venueDetails, setVenueDetails] = useState<Record<number, VenueWithGoogleDetails>>({});
  const [notesEdit, setNotesEdit] = useState<Record<number, string>>({});
  const [savingNotes, setSavingNotes] = useState<number | null>(null);
  const [venueRooms, setVenueRooms] = useState<Record<number, VenueRoomWithConfigs[]>>({});
  const [celebration, setCelebration] = useState<Celebration | null>(null);
  const [dreamVenuePrompt, setDreamVenuePrompt] = useState('');
  const [locationBiasText, setLocationBiasText] = useState('');
  const [locationBiasLatitude, setLocationBiasLatitude] = useState<number | undefined>(undefined);
  const [locationBiasLongitude, setLocationBiasLongitude] = useState<number | undefined>(undefined);
  const [radiusMiles, setRadiusMiles] = useState<number>(10);
  const [selectedFilters, setSelectedFilters] = useState<VenueIntentFilter[]>([]);
  const [derivedTags, setDerivedTags] = useState<VenueIntentFilter[]>([]);
  const [hasManualFilterConfirmation, setHasManualFilterConfirmation] = useState(false);
  const [savedSearches, setSavedSearches] = useState<CelebrationVenueSearch[]>([]);
  const [autoSaveSearch, setAutoSaveSearch] = useState(true);
  const [savingSearch, setSavingSearch] = useState(false);
  const [deletingSearchId, setDeletingSearchId] = useState<number | null>(null);

  useEffect(() => {
    void loadVenues();
  }, [celebrationIdNum]);

  useEffect(() => {
    if (!celebrationIdNum) {
      setCelebration(null);
      setSavedSearches([]);
      return;
    }

    void loadCelebrationContext();
    void loadSavedSearches();
  }, [celebrationIdNum]);

  const loadCelebrationContext = async () => {
    if (!celebrationIdNum) {
      return;
    }

    try {
      const celebrationData = await celebrationService.getCelebration(celebrationIdNum);
      setCelebration(celebrationData);
      setLocationBiasText((prev) => prev || celebrationData.location_name || '');
      setLocationBiasLatitude(celebrationData.latitude);
      setLocationBiasLongitude(celebrationData.longitude);
      setRadiusMiles(celebrationData.radius_miles || 10);
    } catch (err) {
      console.error('Error loading celebration context:', err);
    }
  };

  const loadSavedSearches = async () => {
    if (!celebrationIdNum) {
      return;
    }

    try {
      const searches = await venueService.getCelebrationVenueSearches(celebrationIdNum);
      setSavedSearches(searches);
    } catch (err) {
      console.error('Error loading saved venue searches:', err);
    }
  };

  const loadVenues = async () => {
    try {
      setLoading(true);
      const venuesData = celebrationIdNum
        ? await venueService.getCelebrationVenues(celebrationIdNum)
        : await venueService.getVenues();
      setVenues(venuesData);

      if (celebrationIdNum && venuesData.length > 0) {
        const roomResults = await Promise.all(
          venuesData.map(async (venue) => {
            try {
              const rooms = await venueRoomService.getRooms(venue.id);
              return { venueId: venue.id, rooms };
            } catch {
              return { venueId: venue.id, rooms: [] as VenueRoomWithConfigs[] };
            }
          })
        );

        const roomMap: Record<number, VenueRoomWithConfigs[]> = {};
        roomResults.forEach(({ venueId, rooms }) => {
          roomMap[venueId] = rooms;
        });
        setVenueRooms(roomMap);
      }

      setError(null);
    } catch (err) {
      setError(t('venues:errors.loadVenues'));
      console.error('Error loading venues:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadVenueDetails = async (venueId: number) => {
    if (venueDetails[venueId]) return; // Already loaded

    try {
      setLoadingDetails(prev => ({ ...prev, [venueId]: true }));
      const details = await venueService.getVenueWithDetails(venueId);
      setVenueDetails(prev => ({ ...prev, [venueId]: details }));
    } catch (err) {
      console.error('Error loading venue details:', err);
    } finally {
      setLoadingDetails(prev => ({ ...prev, [venueId]: false }));
    }
  };

  const handleOpenWebsite = async (venue: Venue) => {
    try {
      const cachedWebsite = venue.website || venueDetails[venue.id]?.website;
      if (cachedWebsite) {
        window.open(cachedWebsite, '_blank', 'noopener,noreferrer');
        return;
      }

      setLoadingDetails(prev => ({ ...prev, [venue.id]: true }));
      const details = await venueService.getVenueWithDetails(venue.id);
      setVenueDetails(prev => ({ ...prev, [venue.id]: details }));

      if (details.website) {
        setVenues(prev => prev.map(v => (v.id === venue.id ? { ...v, website: details.website } : v)));
        window.open(details.website, '_blank', 'noopener,noreferrer');
        return;
      }

      alert(t('venues:errors.noWebsiteAvailable'));
    } catch (err) {
      console.error('Error opening venue website:', err);
      alert(t('venues:errors.openWebsite'));
    } finally {
      setLoadingDetails(prev => ({ ...prev, [venue.id]: false }));
    }
  };

  const handleDeleteVenue = async (venueId: number, venueName: string) => {
    if (!confirm(t('venues:dialogs.deleteVenueConfirm', { venueName }))) {
      return;
    }

    try {
      await venueService.deleteVenue(venueId);
      setVenues(prev => prev.filter(v => v.id !== venueId));
      // Remove from details cache
      setVenueDetails(prev => {
        const updated = { ...prev };
        delete updated[venueId];
        return updated;
      });
    } catch (err: any) {
      const message = err?.response?.data?.message || t('venues:errors.deleteVenue');
      alert(t('venues:labels.errorPrefix') + ': ' + message);
    }
  };

  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return '';
    // Simple formatting - can be enhanced
    return phone.replace(/^\+1/, '');
  };

  const handleCombinedPromptChange = (value: string) => {
    setSearchQuery(value);
    setDreamVenuePrompt(value);
    setDerivedTags([]);
    setSelectedFilters([]);
    setHasManualFilterConfirmation(false);
  };

  const hasSearchCriteria = (request?: VenueSearchRequest) => {
    const candidate = request ?? {
      query: searchQuery,
      dreamVenuePrompt,
      selectedFilters,
    };

    return Boolean(candidate.query?.trim() || candidate.dreamVenuePrompt?.trim() || candidate.selectedFilters?.length);
  };

  const buildSearchRequest = (overrides?: Partial<VenueSearchRequest>): VenueSearchRequest => ({
    query: overrides?.query ?? (searchQuery.trim() || undefined),
    dreamVenuePrompt: overrides?.dreamVenuePrompt ?? (dreamVenuePrompt.trim() || undefined),
    locationBiasText: overrides?.locationBiasText ?? (locationBiasText.trim() || undefined),
    locationBiasLatitude: overrides?.locationBiasLatitude ?? locationBiasLatitude,
    locationBiasLongitude: overrides?.locationBiasLongitude ?? locationBiasLongitude,
    radiusMiles: overrides?.radiusMiles ?? radiusMiles,
    selectedFilters: overrides?.selectedFilters ?? selectedFilters,
    celebrationId: overrides?.celebrationId ?? celebrationIdNum,
  });

  const areFiltersEqual = (a: string[], b: string[]) => {
    if (a.length !== b.length) {
      return false;
    }

    const left = [...a].sort();
    const right = [...b].sort();
    return left.every((value, index) => value === right[index]);
  };

  const buildAutoSearchName = (request: VenueSearchRequest) => {
    const source = request.dreamVenuePrompt?.trim() || request.query?.trim() || 'Venue search';
    const base = source.length > 48 ? `${source.slice(0, 48).trim()}...` : source;
    return base;
  };

  const autoSaveCurrentSearch = async (request: VenueSearchRequest, tagsFromResponse: VenueIntentFilter[]) => {
    if (!celebrationIdNum || !autoSaveSearch) {
      return;
    }

    const normalizedQuery = request.query?.trim() || request.dreamVenuePrompt?.trim() || '';
    const normalizedLocation = request.locationBiasText?.trim() || '';
    const normalizedFilters = (request.selectedFilters || []).filter((value): value is VenueIntentFilter => venueIntentOptions.includes(value as VenueIntentFilter));

    const alreadySaved = savedSearches.some((savedSearch) => {
      const savedQuery = (savedSearch.dream_venue_prompt || savedSearch.search_query || '').trim();
      const savedLocation = (savedSearch.location_bias_text || '').trim();
      const savedFilters = savedSearch.selected_filters || [];

      return (
        savedQuery === normalizedQuery &&
        savedLocation === normalizedLocation &&
        Number(savedSearch.radius_miles || 0) === Number(request.radiusMiles || 0) &&
        areFiltersEqual(savedFilters, normalizedFilters)
      );
    });

    if (alreadySaved) {
      return;
    }

    try {
      setSavingSearch(true);
      const savedSearch = await venueService.createCelebrationVenueSearch(celebrationIdNum, {
        name: buildAutoSearchName(request),
        searchQuery: normalizedQuery || undefined,
        dreamVenuePrompt: normalizedQuery || undefined,
        locationBiasText: normalizedLocation || undefined,
        locationBiasLatitude: request.locationBiasLatitude,
        locationBiasLongitude: request.locationBiasLongitude,
        radiusMiles: request.radiusMiles,
        derivedTags: tagsFromResponse,
        selectedFilters: normalizedFilters,
      });
      setSavedSearches((prev) => [savedSearch, ...prev]);
    } catch (err) {
      console.error('Error auto-saving venue search:', err);
    } finally {
      setSavingSearch(false);
    }
  };

  const executeExplore = async (request: VenueSearchRequest, autoApplyDerivedFilters: boolean) => {
    setSearching(true);
    setSearchError(null);

    try {
      const results = await venueService.searchVenues(request);
      setDerivedTags(results.derived_tags || []);

      if (autoApplyDerivedFilters && results.derived_tags.length > 0) {
        setSelectedFilters(results.derived_tags);
      }

      const savedPlaceIds = new Set(
        [
          ...(results.saved_venues || []).map((venue) => venue.google_place_id),
          ...venues.map((venue) => venue.google_place_id),
        ].filter((placeId): placeId is string => typeof placeId === 'string' && placeId.length > 0)
      );

      const mergedResults: SearchSuggestion[] = [
        ...(results.google_suggestions || []).map((venue) => ({
          ...venue,
          is_saved: false,
          matched_parameters: venue.matched_parameters || [],
        })),
      ].filter((venue) => !savedPlaceIds.has(venue.place_id));

      const enrichedResults = await Promise.all(
        mergedResults.map(async (venue) => {
          if (venue.website || !venue.place_id) {
            return venue;
          }

          try {
            const details = await venueService.getVenueByPlaceId(venue.place_id);
            return {
              ...venue,
              website: details.website || undefined,
            };
          } catch {
            return venue;
          }
        })
      );

      setSearchResults(enrichedResults);
      const effectiveRequest: VenueSearchRequest = {
        ...request,
        selectedFilters: autoApplyDerivedFilters && results.derived_tags.length > 0
          ? results.derived_tags
          : request.selectedFilters,
      };
      await autoSaveCurrentSearch(effectiveRequest, results.derived_tags || []);
    } catch (err) {
      console.error('Error exploring venues:', err);
      setSearchError(t('venues:errors.exploreVenues'));
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleExplore = async () => {
    const request = buildSearchRequest();
    if (!hasSearchCriteria(request)) {
      setSearchError(t('venues:errors.missingSearchCriteria'));
      setSearchResults([]);
      return;
    }

    await executeExplore(request, !hasManualFilterConfirmation);
  };

  const refreshSearchResults = async () => {
    if (hasSearchCriteria()) {
      await executeExplore(buildSearchRequest(), false);
    }
  };

  const toggleFilter = (filter: VenueIntentFilter) => {
    setHasManualFilterConfirmation(true);
    setSelectedFilters((prev) => (
      prev.includes(filter)
        ? prev.filter((item) => item !== filter)
        : [...prev, filter]
    ));
  };

  const handleLoadSavedSearch = async (savedSearch: CelebrationVenueSearch) => {
    const nextSelectedFilters = savedSearch.selected_filters || [];
    const restoredPrompt = savedSearch.dream_venue_prompt || savedSearch.search_query || '';
    setSearchQuery(restoredPrompt);
    setDreamVenuePrompt(restoredPrompt);
    setLocationBiasText(savedSearch.location_bias_text || celebration?.location_name || '');
    setLocationBiasLatitude(savedSearch.location_bias_latitude ?? celebration?.latitude);
    setLocationBiasLongitude(savedSearch.location_bias_longitude ?? celebration?.longitude);
    setRadiusMiles(savedSearch.radius_miles || celebration?.radius_miles || 10);
    setDerivedTags(savedSearch.derived_tags || []);
    setSelectedFilters(nextSelectedFilters);
    setHasManualFilterConfirmation(true);
    await executeExplore(buildSearchRequest({
      query: restoredPrompt || undefined,
      dreamVenuePrompt: restoredPrompt || undefined,
      locationBiasText: savedSearch.location_bias_text || celebration?.location_name || undefined,
      locationBiasLatitude: savedSearch.location_bias_latitude ?? celebration?.latitude,
      locationBiasLongitude: savedSearch.location_bias_longitude ?? celebration?.longitude,
      radiusMiles: savedSearch.radius_miles || celebration?.radius_miles || 10,
      selectedFilters: nextSelectedFilters,
    }), false);
  };

  const handleDeleteSavedSearch = async (searchId: number) => {
    if (!celebrationIdNum) {
      return;
    }

    try {
      setDeletingSearchId(searchId);
      await venueService.deleteCelebrationVenueSearch(celebrationIdNum, searchId);
      setSavedSearches((prev) => prev.filter((search) => search.id !== searchId));
    } catch (err) {
      console.error('Error deleting saved venue search:', err);
      setSearchError(t('venues:errors.deleteSavedSearch'));
    } finally {
      setDeletingSearchId(null);
    }
  };

  const handleAssociationToggle = async (venueId: number, isSaved: boolean) => {
    if (!celebrationIdNum) {
      return;
    }

    try {
      setSavingVenueId(venueId);
      if (isSaved) {
        await venueService.removeVenueFromCelebration(celebrationIdNum, venueId);
      } else {
        await venueService.addVenueToCelebration(celebrationIdNum, venueId);
      }
      await loadVenues();
      await refreshSearchResults();
    } catch (err) {
      console.error('Error updating celebration venue association:', err);
      alert(t('venues:errors.updateAssociation'));
    } finally {
      setSavingVenueId(null);
    }
  };

  const handleSaveSearchResult = async (result: SearchSuggestion) => {
    if (!celebrationIdNum) {
      alert(t('venues:errors.openFromCelebration'));
      return;
    }

    try {
      setSavingPlaceId(result.place_id);

      let venueId = result._db_venue_id;
      if (!venueId) {
        const savedVenue = await venueService.ensureVenue(result.place_id);
        venueId = savedVenue.venue_id;
      }

      await venueService.addVenueToCelebration(celebrationIdNum, venueId);
      await loadVenues();
      await refreshSearchResults();
    } catch (err) {
      console.error('Error saving explored venue:', err);
      alert(t('venues:errors.addVenueToCelebration'));
    } finally {
      setSavingPlaceId(null);
    }
  };

  const handleSaveNotes = async (venueId: number) => {
    const newNotes = notesEdit[venueId];
    const currentVenue = venues.find(v => v.id === venueId);
    
    // No change, don't save
    if (newNotes === (currentVenue?.notes || '')) {
      return;
    }

    try {
      setSavingNotes(venueId);
      await venueService.updateVenueNotes(venueId, newNotes || null);
      
      // Update local state
      setVenues(prev => prev.map(v => 
        v.id === venueId ? { ...v, notes: newNotes } : v
      ));
    } catch (err) {
      console.error('Error saving notes:', err);
      alert(t('venues:errors.saveNotes'));
      // Revert
      if (currentVenue) {
        setNotesEdit(prev => ({
          ...prev,
          [venueId]: currentVenue.notes || ''
        }));
      }
    } finally {
      setSavingNotes(null);
    }
  };

  const getCapacityLabel = (room: VenueRoomWithConfigs): string => {
    if (typeof room.max_capacity === 'number' && room.max_capacity > 0) {
      return room.max_capacity.toString();
    }

    const configCapacity = room.seating_configs
      ?.map((cfg) => cfg.capacity)
      .find((capacity): capacity is number => typeof capacity === 'number' && capacity > 0);

    return configCapacity ? configCapacity.toString() : 'N/A';
  };

  const getPlaceDescription = (place: SearchSuggestion): string => {
    const friendlyTypes = (place.types || [])
      .slice(0, 2)
      .map((type) => type.replace(/_/g, ' '));

    if (friendlyTypes.length > 0) {
      const typeText = friendlyTypes.join(' and ');
      if (place.rating) {
        return t('venues:descriptions.typeRated', { typeText, rating: place.rating.toFixed(1) });
      }
        return t('venues:descriptions.typeMatch', { typeText });
    }

    if (place.rating) {
        return t('venues:descriptions.placeRated', { rating: place.rating.toFixed(1) });
    }

      return t('venues:descriptions.placeMatch');
  };

  const renderSavedVenueActions = (venue: Venue) => (
    <div className="flex gap-2 pt-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate(celebrationIdNum ? `/celebrations/${celebrationIdNum}/venues/${venue.id}` : `/venues/${venue.id}`)}
        className="flex-1"
        data-testid="venue-manage-rooms"
      >
        Rooms & Details
      </Button>
      {celebrationIdNum && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleAssociationToggle(venue.id, true)}
          disabled={savingVenueId === venue.id}
          data-testid={`venue-remove-${venue.id}`}
        >
          Remove
        </Button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs />
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('venues:title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {celebrationIdNum
            ? t('venues:descriptions.forCelebration')
            : t('venues:descriptions.general')}
        </p>
      </div>

      {celebrationIdNum && venues.length > 0 && (
        <div className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold">{t('venues:sections.celebrationVenues')}</h2>
            <p className="text-sm text-muted-foreground">{t('venues:descriptions.savedForCelebration')}</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {venues.map((venue) => (
              <Card key={`celebration-${venue.id}`} className="overflow-hidden border-indigo-200 bg-indigo-50/20">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="truncate text-lg">{venue.name}</CardTitle>
                      <CardDescription className="text-sm text-gray-500">{t('venues:status.savedToCelebration')}</CardDescription>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleOpenWebsite(venue)}
                      className="hover:opacity-70 transition-opacity"
                      title={venue.website ? 'Visit website' : 'Try loading website from details'}
                      disabled={loadingDetails[venue.id]}
                    >
                      <Link2
                        className={`h-4 w-4 ${venue.website ? 'text-indigo-500 cursor-pointer' : 'text-gray-400 cursor-pointer'}`}
                      />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {venueRooms[venue.id] && venueRooms[venue.id].length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-600">{t('venues:labels.rooms')}</p>
                      <div className="text-xs text-gray-700 leading-5">
                        {venueRooms[venue.id]
                          .map((room) => `${room.name} (${getCapacityLabel(room)})`)
                          .join(', ')}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 border-t pt-3">
                    <label className="text-sm font-medium text-gray-700">{t('common:labels.notes')}</label>
                    <textarea
                      value={notesEdit[venue.id] !== undefined ? notesEdit[venue.id] : (venue.notes || '')}
                      onChange={(e) => setNotesEdit((prev) => ({
                        ...prev,
                        [venue.id]: e.target.value,
                      }))}
                      onBlur={() => void handleSaveNotes(venue.id)}
                      placeholder={t('venues:placeholders.notes')}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      disabled={savingNotes === venue.id}
                    />
                    {savingNotes === venue.id && <span className="text-xs text-gray-500">{t('common:status.saving')}</span>}
                  </div>

                  {renderSavedVenueActions(venue)}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Search className="h-5 w-5 text-indigo-500" />
            {t('venues:sections.exploreVenues')}
          </CardTitle>
          <CardDescription>
            {t('venues:descriptions.exploreVenues')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {celebrationIdNum && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-900">{t('venues:sections.savedSearches')}</p>
                {savingSearch && <span className="text-xs text-indigo-700">{t('venues:status.savingSearch')}</span>}
              </div>

              {savedSearches.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {savedSearches.map((savedSearch) => (
                    <Card key={savedSearch.id} className="border-dashed">
                      <CardContent className="space-y-3 pt-4">
                        <div className="space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-sm text-slate-900">{savedSearch.name}</h3>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
                              {savedSearch.selected_filters.length} filters
                            </span>
                          </div>
                          {(savedSearch.dream_venue_prompt || savedSearch.search_query) && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {savedSearch.dream_venue_prompt || savedSearch.search_query}
                            </p>
                          )}
                        </div>

                        {savedSearch.selected_filters.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {savedSearch.selected_filters.map((filter) => (
                              <span key={`${savedSearch.id}-${filter}`} className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
                                {filter}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => void handleLoadSavedSearch(savedSearch)}
                          >
                            {t('venues:actions.loadSearch')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => void handleDeleteSavedSearch(savedSearch.id)}
                            disabled={deletingSearchId === savedSearch.id}
                            aria-label={`Delete ${savedSearch.name}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  {t('venues:empty.noSavedSearches')}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-900">{t('venues:labels.searchOrDescribe')}</p>
                <p className="text-xs text-muted-foreground">{t('venues:descriptions.searchHelp')}</p>
              </div>
              {derivedTags.length > 0 && (
                <span className="text-xs text-indigo-700">{t('venues:labels.aiDerivedTags', { count: derivedTags.length })}</span>
              )}
            </div>
            <Textarea
              value={dreamVenuePrompt}
              onChange={(e) => handleCombinedPromptChange(e.target.value)}
              placeholder={t('venues:placeholders.dreamVenuePrompt')}
              className="min-h-[88px]"
            />
          </div>

          <div className="grid gap-3 md:grid-cols-[2fr,120px]">
            <div>
              <p className="mb-1 text-sm font-medium text-slate-900">{t('venues:labels.locationBias')}</p>
              <Input
                value={locationBiasText}
                onChange={(e) => setLocationBiasText(e.target.value)}
                placeholder={celebration?.location_name || t('venues:placeholders.cityOrRegion')}
              />
            </div>
            <div>
              <p className="mb-1 text-sm font-medium text-slate-900">{t('venues:labels.radius')}</p>
              <Input
                type="number"
                min={1}
                max={100}
                value={radiusMiles}
                onChange={(e) => setRadiusMiles(Math.max(1, Number(e.target.value) || 10))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-slate-900">{t('venues:labels.confirmFilters')}</p>
                <p className="text-xs text-muted-foreground">{t('venues:descriptions.confirmFiltersHelp')}</p>
              </div>
            </div>
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
              {venueIntentOptions.map((filter) => {
                const isDerived = derivedTags.includes(filter);
                const isSelected = selectedFilters.includes(filter);
                return (
                  <label
                    key={filter}
                    className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${isSelected ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white'}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleFilter(filter)}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="flex-1">{filter}</span>
                    {isDerived && <span className="text-[10px] uppercase tracking-wide text-indigo-700">AI</span>}
                  </label>
                );
              })}
            </div>
          </div>

          {celebrationIdNum && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={autoSaveSearch}
                  onChange={(e) => setAutoSaveSearch(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                {t('venues:labels.autoSaveAfterExplore')}
              </label>
              <p className="mt-1 text-xs text-muted-foreground">
                {t('venues:descriptions.autoSaveHelp')}
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => void handleExplore()} disabled={searching}>
              <Sparkles className="mr-2 h-4 w-4" />
              {searching ? t('venues:status.exploring') : t('venues:actions.explore')}
            </Button>
          </div>

          {searchError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {searchError}
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-900">{t('venues:sections.exploreResults')}</h2>
                <span className="text-xs text-muted-foreground">{t('venues:labels.resultsCount', { count: searchResults.length })}</span>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {searchResults.map((result) => {
                  return (
                    <Card key={result.place_id} className="border-dashed">
                      <CardContent className="space-y-3 pt-4">
                        <div className="space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-sm text-slate-900">{result.name}</h3>
                          </div>
                          {result.formatted_address && (
                            <p className="text-xs text-muted-foreground">{result.formatted_address}</p>
                          )}
                        </div>

                        <p className="text-xs text-slate-600">{getPlaceDescription(result)}</p>

                        <div className="flex flex-wrap gap-1">
                          {(result.types || []).slice(0, 3).map((type) => (
                            <span key={type} className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
                              {type.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>

                        {result.matched_parameters && result.matched_parameters.length > 0 && (
                          <div className="rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
                            {result.matched_parameters.join(', ')}
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => handleSaveSearchResult(result)}
                            disabled={savingPlaceId === result.place_id}
                          >
                            <Link2 className="mr-2 h-4 w-4" />
                            {savingPlaceId === result.place_id ? t('common:status.saving') : t('venues:actions.addToCelebration')}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Saved Venues List */}
      {!celebrationIdNum && (
        <>
          <div>
            <h2 className="mb-4 text-lg font-semibold">{t('venues:sections.allSavedVenues')}</h2>
          </div>
                              <label className="text-sm font-medium text-gray-700">{t('venues:labels.notes')}</label>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 venue-container" data-testid="venue-grid">
            {venues.map((venue) => {
          const details = venueDetails[venue.id];
          const isLoadingDetails = loadingDetails[venue.id];

          return (
            <Card key={venue.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">{venue.name}</CardTitle>
                    <CardDescription className="text-sm text-gray-500">
                      {t('venues:labels.addedOn', { date: new Date(venue.created_at).toLocaleDateString() })}
                    </CardDescription>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleOpenWebsite(venue)}
                    className="hover:opacity-70 transition-opacity mr-2"
                    title={venue.website ? 'Visit website' : 'Try loading website from details'}
                    disabled={loadingDetails[venue.id]}
                  >
                    <Link2
                      className={`h-4 w-4 ${venue.website ? 'text-indigo-500 cursor-pointer' : 'text-gray-400 cursor-pointer'}`}
                    />
                  </button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteVenue(venue.id, venue.name)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Website link - always show if available */}
                {venue.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-gray-400" />
                    <a
                      href={venue.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 truncate flex-1"
                      title={venue.website}
                    >
                      {t('venues:actions.visitWebsite')}
                    </a>
                  </div>
                )}

                {!details && !isLoadingDetails && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadVenueDetails(venue.id)}
                    className="w-full"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    {t('venues:actions.loadDetails')}
                  </Button>
                )}

                {isLoadingDetails && (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-600">{t('venues:status.loadingDetails')}</span>
                  </div>
                )}

                {details && (
                  <div className="space-y-3">
                    {/* Address */}
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-gray-600">
                        {details.formatted_address}
                      </div>
                    </div>

                    {/* Rating */}
                    {details.rating && (
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-400" />
                        <span className="text-sm font-medium">{details.rating}</span>
                        {details.user_ratings_total && (
                          <span className="text-sm text-gray-500">
                            ({details.user_ratings_total} reviews)
                          </span>
                        )}
                      </div>
                    )}

                    {/* Phone */}
                    {details.formatted_phone_number && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {formatPhoneNumber(details.formatted_phone_number)}
                        </span>
                      </div>
                    )}

                    {/* Website */}
                    {details.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-gray-400" />
                        <a
                          href={details.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 truncate"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}

                    {/* Opening Hours */}
                    {details.opening_hours?.weekday_text && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium">{t('venues:labels.hours')}</span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-0.5 ml-6">
                          {details.opening_hours.weekday_text.slice(0, 3).map((hour, index) => (
                            <div key={index}>{hour}</div>
                          ))}
                          {details.opening_hours.weekday_text.length > 3 && (
                            <div className="text-gray-500">
                              {t('venues:labels.moreDays', { count: details.opening_hours.weekday_text.length - 3 })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Types */}
                    {details.types && details.types.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {details.types
                          .filter(type => !['point_of_interest', 'establishment'].includes(type))
                          .slice(0, 3)
                          .map((type) => (
                            <span
                              key={type}
                              className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                            >
                              {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          ))}
                      </div>
                    )}
                    
                    {/* Notes */}
                    <div className="space-y-2 border-t pt-3">
                      <label className="text-sm font-medium text-gray-700">{t('common:labels.notes')}</label>
                      <textarea
                        value={notesEdit[venue.id] !== undefined ? notesEdit[venue.id] : (venue.notes || '')}
                        onChange={(e) => setNotesEdit(prev => ({
                          ...prev,
                          [venue.id]: e.target.value
                        }))}
                        onBlur={() => handleSaveNotes(venue.id)}
                        placeholder={t('venues:placeholders.notes')}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={3}
                        disabled={savingNotes === venue.id}
                      />
                      {savingNotes === venue.id && (
                        <span className="text-xs text-gray-500">{t('common:status.saving')}</span>
                      )}
                    </div>
                    
                    {renderSavedVenueActions(venue)}
                  </div>
                )}
              </CardContent>
            </Card>
          );
            })}
          </div>
        </>
      )}

      {venues.length === 0 && !loading && (
        <div className="text-center py-12">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('venues:empty.noVenues')}</h3>
          <p className="text-gray-600 mb-4">
            {celebrationIdNum
              ? t('venues:empty.noVenuesForCelebration')
              : t('venues:empty.noVenuesGeneral')}
          </p>
          {!celebrationIdNum && (
            <Button onClick={() => navigate('/events/new')}>
              {t('venues:actions.createFirstEvent')}
            </Button>
          )}
        </div>
      )}
    </PageContainer>
  );
};
